import type { TimeEntryPagesParams } from "@common/api/time-entries"
import type { TimeEntryStatus } from "@common/client"
import { localTimeZone } from "@common/lib/date-utils"
import { startOfDay, startOfMonth, startOfWeek } from "date-fns"
import { z } from "zod"
import {
  DEFAULT_GROUPING,
  type Grouping,
  groupChoiceSchema,
  groupingSchema,
} from "./grouping"

export const RANGES = ["all", "today", "week", "month"] as const

const rangeSchema = z.enum(RANGES)

export type Range = z.infer<typeof rangeSchema>

export const STATUSES = [
  "running",
  "logged",
] as const satisfies readonly TimeEntryStatus[]

export const timesheetSearchSchema = z.object({
  range: rangeSchema.optional().catch(undefined),
  group: groupChoiceSchema.optional().catch(undefined),
  subgroup: groupingSchema.optional().catch(undefined),
  work_order: z.array(z.string()).optional().catch(undefined),
  aircraft: z.array(z.string()).optional().catch(undefined),
  status: z.array(z.enum(STATUSES)).optional().catch(undefined),
})

export type TimesheetSearch = z.infer<typeof timesheetSearchSchema>

export type TimesheetDisplay = {
  group: Grouping | undefined
  subgroup: Grouping | undefined
}

export function rangeLabel(range: Range): string {
  switch (range) {
    case "all":
      return "All"
    case "today":
      return "Today"
    case "week":
      return "This week"
    case "month":
      return "This month"
  }
}

function rangeStart(range: Range, now: Date): Date | null {
  switch (range) {
    case "all":
      return null
    case "today":
      return startOfDay(now)
    case "week":
      return startOfWeek(now, { weekStartsOn: 1 })
    case "month":
      return startOfMonth(now)
  }
}

export function sinceOf(search: TimesheetSearch): string | null {
  return rangeStart(search.range ?? "all", new Date())?.toISOString() ?? null
}

export function displayOf(search: TimesheetSearch): TimesheetDisplay {
  const group =
    search.group === "none" ? undefined : (search.group ?? DEFAULT_GROUPING)
  return {
    group,
    subgroup: group && search.subgroup !== group ? search.subgroup : undefined,
  }
}

export function groupingSearch(
  display: TimesheetDisplay,
): Partial<TimesheetSearch> {
  return {
    group:
      display.group === DEFAULT_GROUPING
        ? undefined
        : (display.group ?? "none"),
    subgroup: display.subgroup,
  }
}

export function timesheetPagesParams(
  search: TimesheetSearch,
): TimeEntryPagesParams {
  const display = displayOf(search)
  return {
    since: sinceOf(search),
    time_zone: localTimeZone(),
    work_package_id: search.work_order?.length ? search.work_order : null,
    aircraft: search.aircraft?.length ? search.aircraft : null,
    status: search.status?.length ? search.status : null,
    group: display.group ?? null,
    subgroup: display.subgroup ?? null,
  }
}
